import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Badge, Button, Input, ProgressBar,
  MessageBar, MessageBarBody, MessageBarTitle,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import { useAsync } from '../api/useAsync';
import { Loading, ErrorState, Empty, SeverityBadge } from './common';
import { useCustomer } from '../context/CustomerContext';
import type { ControlCoverageStatus } from '../api/client';

const PAGE = 50;

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
  stats: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    columnGap: tokens.spacingHorizontalL, rowGap: tokens.spacingVerticalM,
  },
  stat: { display: 'flex', flexDirection: 'column', rowGap: '2px' },
  metric: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: '1' },
  label: {
    color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: tokens.fontWeightSemibold,
  },
  filters: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalS, rowGap: tokens.spacingVerticalS },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  ref: { fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  finding: {
    display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalXS, 0),
  },
  pager: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalM },
});

const STATUS_LABEL: Record<ControlCoverageStatus, string> = {
  'findings': 'Findings raised',
  'clear': 'Nothing raised',
  'not-available': 'Could not be checked',
  'not-applicable': 'Out of scope',
  'not-assessed': 'Not assessed',
};

/**
 * What a collection says about one framework.
 *
 * Three states rather than pass and fail. "Not assessed" is the one that keeps
 * this honest: the ISM has 1,192 controls and this assessment measures 33
 * control outcomes, so most of it is simply out of view. Counting those as
 * compliant would be a false statement in a customer deliverable.
 */
/** Records that a control is beyond what this tool can see. */
function MarkOutOfScope({ frameworkId, controlRef, current, onSaved }: {
  frameworkId: string; controlRef: string; current: boolean; onSaved: () => void;
}) {
  const api = useApi();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (applicability: 'out-of-scope' | 'applicable') => {
    setBusy(true); setError(null);
    try {
      await api.setApplicability(frameworkId, controlRef, applicability,
        applicability === 'applicable' ? 'Returned to scope' : reason.trim());
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (current) {
    return (
      <Button size="small" appearance="subtle" disabled={busy}
              onClick={() => void save('applicable')}>
        Return to scope
      </Button>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
      <Input size="small" value={reason} placeholder="Why is this beyond the tool's reach?"
             onChange={(_, d) => setReason(d.value)} style={{ minWidth: '280px' }} />
      <Button size="small" disabled={busy || reason.trim().length === 0}
              onClick={() => void save('out-of-scope')}>
        Mark out of scope
      </Button>
      {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
    </div>
  );
}

export function FrameworkCoverage({ frameworkId }: { frameworkId: string }) {
  const styles = useStyles();
  const api = useApi();
  const { tenant, run } = useCustomer();
  const [status, setStatus] = useState<ControlCoverageStatus | null>('findings');
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState<string[]>([]);

  const coverage = useAsync(
    () => (tenant && run
      ? api.getFrameworkCoverage(tenant.tenantId, run.runId, frameworkId,
          { status: status ?? '', skip, take: PAGE })
      : Promise.resolve(null)),
    [tenant?.tenantId, run?.runId, frameworkId, status, skip],
  );

  if (!tenant) return <Empty>Select a customer to see how this framework was assessed.</Empty>;
  if (!run) return <Empty>No collection has run for {tenant.displayName} yet.</Empty>;
  if (coverage.loading && !coverage.data) return <Loading label="Working out coverage" />;
  if (coverage.error) return <ErrorState error={coverage.error} />;
  if (!coverage.data) return <Empty>No coverage available.</Empty>;

  const { summary, total, controls } = coverage.data;
  const assessedCount = summary.withFindings + summary.clear;
  const shownTo = Math.min(skip + PAGE, total);

  const pick = (s: ControlCoverageStatus | null) => { setStatus(s); setSkip(0); };

  return (
    <div className={styles.wrap}>
      {summary.runStatus === 'failed' && (
        <MessageBar intent="error">
          <MessageBarBody>
            This collection failed and is incomplete. Controls shown as raising nothing may
            simply not have been collected.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.metric}>{summary.totalControls}</span>
          <span className={styles.label}>Controls</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.metric} style={{ color: tokens.colorStatusDangerForeground1 }}>
            {summary.withFindings}
          </span>
          <span className={styles.label}>Findings raised</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.metric} style={{ color: tokens.colorStatusSuccessForeground1 }}>
            {summary.clear}
          </span>
          <span className={styles.label}>Nothing raised</span>
        </div>
        <div className={styles.stat}>
          {/* Separated from "nothing raised" deliberately. A tenant without
              Defender licensing produces no Defender findings, and counting
              that as clear would overstate the result. */}
          <span className={styles.metric} style={{ color: tokens.colorStatusWarningForeground1 }}>
            {summary.notAvailable}
          </span>
          <span className={styles.label}>Could not check</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.metric} style={{ color: tokens.colorNeutralForeground3 }}>
            {summary.notApplicable}
          </span>
          <span className={styles.label}>Out of scope</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.metric} style={{ color: tokens.colorNeutralForeground3 }}>
            {summary.notAssessed}
          </span>
          <span className={styles.label}>Not assessed</span>
        </div>
      </div>

      <ProgressBar
        value={summary.totalControls === 0 ? 0 : assessedCount / summary.totalControls}
        thickness="large"
      />

      {/* The number a customer will ask about, stated before they have to. */}
      <MessageBar intent={summary.notAssessed > assessedCount ? 'warning' : 'info'}>
        <MessageBarBody>
          <MessageBarTitle>
            This assessment reaches {assessedCount} of {summary.totalControls} controls
          </MessageBarTitle>
          {summary.notAvailable > 0 && (
            <>
              A further {summary.notAvailable} could not be checked in this tenant, usually
              because of licensing or a permission that was refused. Those are gaps in what
              this collection could see, not in the customer's configuration.{' '}
            </>
          )}
          The remaining {summary.notAssessed} are not measured by any check in this
          assessment, so nothing is claimed about them either way. A control showing nothing
          raised is not evidence of compliance: it means this collection found nothing
          against it.
        </MessageBarBody>
      </MessageBar>

      <div className={styles.filters}>
        <Button size="small" appearance={status === null ? 'primary' : 'secondary'}
                onClick={() => pick(null)}>
          All ({summary.totalControls})
        </Button>
        <Button size="small" appearance={status === 'findings' ? 'primary' : 'secondary'}
                onClick={() => pick('findings')}>
          Findings raised ({summary.withFindings})
        </Button>
        <Button size="small" appearance={status === 'clear' ? 'primary' : 'secondary'}
                onClick={() => pick('clear')}>
          Nothing raised ({summary.clear})
        </Button>
        <Button size="small" appearance={status === 'not-available' ? 'primary' : 'secondary'}
                onClick={() => pick('not-available')}>
          Could not check ({summary.notAvailable})
        </Button>
        <Button size="small" appearance={status === 'not-applicable' ? 'primary' : 'secondary'}
                onClick={() => pick('not-applicable')}>
          Out of scope ({summary.notApplicable})
        </Button>
        <Button size="small" appearance={status === 'not-assessed' ? 'primary' : 'secondary'}
                onClick={() => pick('not-assessed')}>
          Not assessed ({summary.notAssessed})
        </Button>
        <span className={styles.muted}>
          {total === 0 ? 'none' : `${skip + 1} to ${shownTo} of ${total}`}
        </span>
      </div>

      {controls.length === 0 ? (
        <Empty>No control in this framework has that status.</Empty>
      ) : (
        <>
          <Accordion multiple collapsible openItems={open}
                     onToggle={(_, d) => setOpen(d.openItems as string[])}>
            {controls.map((c) => (
              <AccordionItem key={c.controlRef} value={c.controlRef}>
                <AccordionHeader>
                  <div className={styles.head}>
                    <span className={styles.ref}>{c.controlRef}</span>
                    <Text>{c.title}</Text>
                    <Badge
                      appearance="tint"
                      size="small"
                      color={c.status === 'findings' ? 'danger'
                           : c.status === 'clear' ? 'success' : 'subtle'}
                    >
                      {STATUS_LABEL[c.status]}
                    </Badge>
                    {c.findings.length > 0 && (
                      <Badge appearance="outline" size="small">{c.findings.length}</Badge>
                    )}
                  </div>
                </AccordionHeader>

                <AccordionPanel>
                  {/* The publisher's own wording. The heading is one sentence
                      of it, so the rest belongs here rather than nowhere. */}
                  {c.description && (
                    <Text size={300} block style={{ marginBottom: '8px' }}>
                      {c.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                    </Text>
                  )}
                  {c.status === 'not-applicable' ? (
                    <div>
                      <Text className={styles.muted}>
                        Beyond what this assessment can reach. {c.unavailableReason}
                      </Text>
                      <MarkOutOfScope frameworkId={frameworkId} controlRef={c.controlRef}
                                      current onSaved={() => coverage.reload()} />
                    </div>
                  ) : c.status === 'not-assessed' ? (
                    <div>
                      <Text className={styles.muted}>
                        No check in this assessment maps to this control, so this collection
                        says nothing about it. That is a gap in coverage, not a pass.
                      </Text>
                      {/* Some controls are gaps that could be closed with a mapping;
                          others are simply beyond a cloud assessment. Only a person
                          can tell which, so only a person records it. */}
                      <MarkOutOfScope frameworkId={frameworkId} controlRef={c.controlRef}
                                      current={false} onSaved={() => coverage.reload()} />
                    </div>
                  ) : c.status === 'not-available' ? (
                    <Text className={styles.muted}>
                      A check maps to this control, but this collection could not read the
                      data it needs, so nothing is claimed either way. {c.unavailableReason}
                    </Text>
                  ) : c.findings.length === 0 ? (
                    <Text className={styles.muted}>
                      Assessed through {c.primitiveIds.join(', ')}. Nothing was raised in this
                      collection, which is weaker than evidence of compliance.
                    </Text>
                  ) : (
                    <div>
                      <Text className={styles.muted}>
                        Assessed through {c.primitiveIds.join(', ')}.
                      </Text>
                      {c.findings.map((f) => (
                        <div key={f.findingId} className={styles.finding}>
                          <SeverityBadge severity={f.severity} />
                          <Text size={300}>{f.title}</Text>
                          <Badge appearance="outline" size="small">{f.checkId}</Badge>
                          {f.dispositionStatus && f.dispositionStatus !== 'open' && (
                            <Badge appearance="tint" size="small"
                                   color={f.dispositionStatus === 'remediated' ? 'success' : 'informative'}>
                              {f.dispositionStatus.replace('-', ' ')}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          <div className={styles.pager}>
            <Button size="small" disabled={skip === 0}
                    onClick={() => setSkip(Math.max(0, skip - PAGE))}>Previous</Button>
            <Button size="small" disabled={shownTo >= total}
                    onClick={() => setSkip(skip + PAGE)}>Next</Button>
          </div>
        </>
      )}
    </div>
  );
}
